import { useContext } from "react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { AuthContext } from "./context/auth.context";
import { UserInfoProvider } from "./context/userInfo.context";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { Landing } from "./pages/Landing";
import { Home } from "./pages/Home";
import { RequestDetail } from "./pages/RequestDetail";
import { PolicyCheckDetail } from "./pages/PolicyCheckDetail";
import { PolicyCheckResult } from "./pages/PolicyCheckResult";
import { SecurityPolicy } from "./pages/SecurityPolicy";
import { SetPassword } from "./pages/SetPassword";
import { ConfirmEmail } from "./pages/ConfirmEmail";
import { LicenseAgreement } from "./pages/LicenseAgreement";
import { Profile } from "./pages/Profile";
import { Tariffs } from "./pages/Tariffs";
import { CircularProgress } from "./components/CircularProgress/CircularProgress";
import { CookieConsentBanner } from "./components/CookieConsentBanner/CookieConsentBanner";
import { Header } from "./components/Header/Header";
import { PaymentResultModal } from "./components/PaymentResultModal/PaymentResultModal";
import { LICENSE_AGREEMENT_ROUTE } from "./utils/licenseAgreement";
import { COMPANY_REQUISITES } from "./data/companyContacts";
import { FOOTER_DOCS, getFooterDocHref } from "./data/footerDocs";

function App() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const hideFooter = location.pathname === LICENSE_AGREEMENT_ROUTE;

  if (user === undefined) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size="lg" />
      </div>
    );
  }

  const authedRoutes = (
    <Routes>
      <Route index element={<Home />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/tariffs" element={<Tariffs />} />
      <Route path="/request/:id" element={<RequestDetail />} />
      <Route path="/it-check/:id" element={<RequestDetail kind="it" />} />
      <Route path="/policy-check/result/:fileId" element={<PolicyCheckResult />} />
      <Route path="/policy-check/:id" element={<PolicyCheckDetail />} />
      <Route path="/auth/password/:id" element={<SetPassword />} />
      <Route path="/auth/confirm/:id" element={<ConfirmEmail />} />
      <Route path="/security-policy" element={<SecurityPolicy />} />
      <Route path="/license-agreement" element={<LicenseAgreement />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  return (
    <div className="app-shell">
      <Header />
      <CookieConsentBanner />
      <PaymentResultModal />
      <main className="app-main">
        {user ? (
          <UserInfoProvider>{authedRoutes}</UserInfoProvider>
        ) : (
          <Routes>
            <Route index element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/password/:id" element={<SetPassword />} />
            <Route path="/auth/confirm/:id" element={<ConfirmEmail />} />
            <Route path="/security-policy" element={<SecurityPolicy />} />
            <Route path="/license-agreement" element={<LicenseAgreement />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </main>
      {!hideFooter && (
        <footer id="contacts" className="app-footer">
          <div className="app-footer-content">
            <p className="app-footer-title">Контакты и реквизиты</p>
            {COMPANY_REQUISITES.map((company) => (
              <section key={company.roleLabel} className="app-footer-company">
                <h3 className="app-footer-company-role">{company.roleLabel}</h3>
                <dl className="app-footer-contacts">
                  <div className="app-footer-row">
                    <dt>Полное фирменное наименование организации</dt>
                    <dd>{company.fullLegalName}</dd>
                  </div>
                  <div className="app-footer-row">
                    <dt>Адрес местонахождения</dt>
                    <dd>{company.legalAddress}</dd>
                  </div>
                  {company.postalAddress ? (
                    <div className="app-footer-row">
                      <dt>Почтовый адрес</dt>
                      <dd>{company.postalAddress}</dd>
                    </div>
                  ) : null}
                  <div className="app-footer-row">
                    <dt>Основной государственный регистрационный номер</dt>
                    <dd>{company.ogrn}</dd>
                  </div>
                  <div className="app-footer-row">
                    <dt>ИНН</dt>
                    <dd>{company.inn}</dd>
                  </div>
                  <div className="app-footer-row">
                    <dt>КПП</dt>
                    <dd>{company.kpp}</dd>
                  </div>
                  <div className="app-footer-row">
                    <dt>{company.directorLabel}</dt>
                    <dd>{company.director}</dd>
                  </div>
                  {company.bankAccount ? (
                    <div className="app-footer-row">
                      <dt>Расчетный счет</dt>
                      <dd>{company.bankAccount}</dd>
                    </div>
                  ) : null}
                  {company.bankName ? (
                    <div className="app-footer-row">
                      <dt>Банк</dt>
                      <dd>{company.bankName}</dd>
                    </div>
                  ) : null}
                  {company.bik ? (
                    <div className="app-footer-row">
                      <dt>БИК</dt>
                      <dd>{company.bik}</dd>
                    </div>
                  ) : null}
                  {company.corrAccount ? (
                    <div className="app-footer-row">
                      <dt>Корр. счет</dt>
                      <dd>{company.corrAccount}</dd>
                    </div>
                  ) : null}
                  {company.phone && company.phoneHref ? (
                    <div className="app-footer-row">
                      <dt>Контактные телефоны</dt>
                      <dd>
                        <a href={company.phoneHref}>{company.phone}</a>
                      </dd>
                    </div>
                  ) : null}
                  {company.email ? (
                    <div className="app-footer-row">
                      <dt>Адрес электронной почты</dt>
                      <dd>
                        <a href={`mailto:${company.email}`}>{company.email}</a>
                      </dd>
                    </div>
                  ) : null}
                </dl>
              </section>
            ))}
            <Link to={LICENSE_AGREEMENT_ROUTE} className="app-footer-link">
              Пользовательское соглашение
            </Link>
            <div className="app-footer-docs">
              {FOOTER_DOCS.map((doc) => (
                <a
                  key={doc.fileName}
                  href={getFooterDocHref(doc.fileName)}
                  className="app-footer-link app-footer-docs-link"
                  download
                >
                  {doc.label}
                </a>
              ))}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

export default App;
